import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    Star, Search, Trophy, Target, Info, PlusCircle, Edit3, Shield, Bookmark,
    Bell, User, MessageCircle, LogOut, ChevronDown, X
} from 'lucide-react';
import ProfileImage from './ProfileImage';

const Header = ({
    showQuickActions,
    setShowQuickActions,
    setShowQuickReview,
    setShowProviderSystem,
    // showNotifications,
    // setShowNotifications,
    isLoggedIn,
    isVerified,
    userProfile,
    showProfileDropdown,
    setShowProfileDropdown,
    setShowUserReviews,
    handleLogout,
    setShowVerifyModal,
    setShowAuthModal,
    setAuthMode,
    userReviews
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [showNotifications, setShowNotifications] = useState(false);

    // Helper function to check if current path is active
    const isActivePath = (path) => {
        return location.pathname === path;
    };

    // Navigation items with their routes
    const navItems = [
        { id: 'home', label: 'Discover', icon: Search, path: '/' },
        { id: 'leaderboard', label: 'Top Rated', icon: Trophy, path: '/leaderboard' },
        { id: 'categories', label: 'Categories', icon: Target, path: '/categories' },
        { id: 'how-it-works', label: 'How It Works', icon: Info, path: '/how-it-works' }
    ];

    // const [notifications, setNotifications] = useState([])
    const [notifications, setNotifications] = useState([
        {
            message: "Your profile was updated successfully.",
            timestamp: new Date(Date.now() - 1000 * 60 * 5).toLocaleString()
        },
        {
            message: "New review received for your service.",
            timestamp: new Date(Date.now() - 1000 * 60 * 30).toLocaleString()
        },
        {
            message: "Welcome to IGReview! Start by updating your profile.",
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toLocaleString()
        }
    ]);

    return (
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                <div className="flex justify-between items-center h-14 sm:h-16">
                    {/* Logo and Navigation */}
                    <div className="flex items-center space-x-2 sm:space-x-8">
                        <Link to="/" className="flex items-center space-x-1 sm:space-x-2 cursor-pointer">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                                <Star className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="currentColor" />
                            </div>
                            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                IGReview
                            </span>
                        </Link>

                        <nav className="hidden md:flex space-x-6">
                            {navItems.map(item => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.id}
                                        to={item.path}
                                        className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-all ${isActivePath(item.path)
                                            ? 'bg-purple-100 text-purple-700 font-medium'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                            }`}
                                    >
                                        <Icon size={16} />
                                        <span>{item.label}</span>
                                    </Link>
                                );
                            })}
                        </nav>
                    </div>

                    {/* User Actions */}
                    <div className="flex items-center space-x-1 sm:space-x-4">
                        {/* Quick Actions Button */}
                        <button
                            onClick={() => setShowQuickActions(!showQuickActions)}
                            className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative"
                        >
                            <PlusCircle size={18} className="sm:w-5 sm:h-5" />
                            {showQuickActions && (
                                <div className="absolute top-full right-0 mt-2 w-44 sm:w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                                    <button
                                        onClick={() => {
                                            setShowQuickReview(true);
                                            setShowQuickActions(false);
                                        }}
                                        className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-sm sm:text-base"
                                    >
                                        <Edit3 size={14} className="sm:w-4 sm:h-4" />
                                        <span>Write Review</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowProviderSystem(true);
                                            setShowQuickActions(false);
                                        }}
                                        className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-sm sm:text-base"
                                    >
                                        <Shield size={14} className="sm:w-4 sm:h-4" />
                                        <span>Provider Dashboard</span>
                                    </button>
                                    <button className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-sm sm:text-base">
                                        <Bookmark size={14} className="sm:w-4 sm:h-4" />
                                        <span>Saved Services</span>
                                    </button>
                                </div>
                            )}
                        </button>

                        {/* Notifications */}
                        {/* <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative"
                        >
                            <Bell size={20} />
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                        </button> */}

                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="relative p-1.5 sm:p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                        >
                            <Bell size={18} className="sm:w-5 sm:h-5" />
                            {notifications?.length > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                                    {notifications.length > 9 ? '9+' : notifications.length}
                                </span>
                            )}
                        </button>

                        {showNotifications && (
                            <div className="fixed top-14 sm:top-16 right-2 sm:right-4 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96">
                                <div className="p-3 sm:p-4">
                                    <h3 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Notifications</h3>
                                    {notifications?.length > 0 ? (
                                        <div className="space-y-2 sm:space-y-3 max-h-64 overflow-y-auto">
                                            {notifications.map((notification, index) => (
                                                <div key={index} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 rounded-lg">
                                                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <Bell size={12} className="sm:w-4 sm:h-4 text-purple-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs sm:text-sm text-gray-800 break-words">{notification.message}</p>
                                                        <p className="text-xs text-gray-500 mt-1">{notification.timestamp}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-6 sm:py-8 text-sm">No new notifications</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* User Profile */}
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            {isLoggedIn || isVerified ? (
                                <div className="relative profile-dropdown">
                                    <div
                                        className="flex items-center space-x-1 sm:space-x-3 cursor-pointer group p-1 sm:p-0"
                                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                                    >
                                        <ProfileImage
                                            profilePicture={userProfile?.profilePicture || userProfile?.profilePictureUrl || `https://ui-avatars.com/api/?name=${String(userProfile?.name || userProfile?.fullName || 'U').charAt(0)}&background=7c3aed&color=fff&size=32`}
                                            profile_pic_base64={userProfile?.profile_pic_base64}
                                            alt={String(userProfile?.name || userProfile?.fullName || "User")}
                                            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-purple-200"
                                        />
                                        <div className="hidden sm:flex flex-col">
                                            <span className="text-sm font-medium text-gray-700">
                                                {(() => {
                                                    // If user signed up normally, show their name
                                                    if (userProfile?.name || userProfile?.fullName) {
                                                        return String(userProfile.name || userProfile.fullName);
                                                    }
                                                    // If verified by Instagram, show Instagram username
                                                    if (userProfile?.instagramUsername) {
                                                        return String(userProfile.instagramUsername);
                                                    }
                                                    // If verified by email, show email without domain
                                                    if (userProfile?.email) {
                                                        return String(userProfile.email.split('@')[0]);
                                                    }
                                                    // Default fallback
                                                    return "User";
                                                })()}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {(() => {
                                                    // If user has an Instagram username, show it
                                                    if (userProfile?.instagramUsername) {
                                                        return `@${userProfile.instagramUsername}`;
                                                    }
                                                    // If user has a regular username
                                                    if (userProfile?.username) {
                                                        return `@${userProfile.username}`;
                                                    }
                                                    // For email verified users, show email without domain
                                                    if (userProfile?.email) {
                                                        return `@${userProfile.email.split('@')[0]}`;
                                                    }
                                                    // Default fallback
                                                    return "@user";
                                                })()}
                                            </span>
                                        </div>
                                        <ChevronDown size={12} className="sm:w-4 sm:h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                                    </div>

                                    {/* Dropdown Menu */}
                                    {showProfileDropdown && (
                                        <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                            {(isLoggedIn || isVerified) && (
                                                <>
                                                    <Link
                                                        to="/myreviews"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowUserReviews(true);
                                                            setShowProfileDropdown(false);
                                                        }}
                                                        className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-1 sm:space-x-2"
                                                    >
                                                        <MessageCircle size={14} className="sm:w-4 sm:h-4" />
                                                        <span className="truncate">My Reviews{userReviews.length > 0 ? ` (${userReviews.length})` : ''}</span>
                                                    </Link>
                                                    <Link
                                                        to="/myprofile"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowProfileDropdown(false);
                                                        }}
                                                        className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-1 sm:space-x-2"
                                                    >
                                                        <User size={14} className="sm:w-4 sm:h-4" />
                                                        <span>My Profile</span>
                                                    </Link>
                                                </>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLogout();
                                                    setShowProfileDropdown(false);
                                                }}
                                                className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-1 sm:space-x-2"
                                            >
                                                <LogOut size={14} className="sm:w-4 sm:h-4" />
                                                <span>Sign Out</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center space-x-1 sm:space-x-3">
                                    {/* Verify Me Button */}
                                    <button
                                        onClick={() => {
                                            console.log('Verify Me button clicked');
                                            setAuthMode('verify');
                                            setShowVerifyModal(true);
                                        }}
                                        className="border border-purple-600 text-purple-600 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium hover:bg-purple-50 transition-all text-xs sm:text-sm"
                                    >
                                        <span className="hidden sm:inline">Verify Me</span>
                                        <span className="sm:hidden">Verify</span>
                                    </button>

                                    {/* Sign In Button */}
                                    <button
                                        onClick={() => {
                                            console.log('Sign In button clicked!');
                                            setAuthMode('signin');
                                            setShowAuthModal(true);
                                        }}
                                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium hover:shadow-lg transition-all text-xs sm:text-sm"
                                    >
                                        Sign In
                                    </button>

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;